import { requireSupabase } from "./supabase";

const safeName = (name: string) =>
  name
    .normalize("NFKD")
    .replace(/[^\w.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();

export async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

export async function uploadProjectFile(
  projectId: string,
  organizationId: string,
  userId: string,
  revisionId: string,
  file: File,
): Promise<{ path: string; checksum: string }> {
  const client = requireSupabase();
  const checksum = await sha256(file);
  const path = `${projectId}/${revisionId}/${Date.now()}-${safeName(file.name)}`;
  const { error } = await client.storage.from("project-files").upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || "application/octet-stream",
  });
  if (error) throw error;

  const { error: metadataError } = await client.from("project_file_metadata").insert({
    project_id: projectId,
    organization_id: organizationId,
    revision_key: revisionId,
    storage_path: path,
    file_name: file.name,
    mime_type: file.type || "application/octet-stream",
    file_size: file.size,
    sha256: checksum,
    uploaded_by: userId,
  });
  if (metadataError) {
    await client.storage.from("project-files").remove([path]);
    throw metadataError;
  }

  return { path, checksum };
}

export async function createProjectFileUrl(path: string): Promise<string> {
  const client = requireSupabase();
  const { data, error } = await client.storage.from("project-files").createSignedUrl(path, 60);
  if (error) throw error;
  return data.signedUrl;
}
